<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import LoadingBar from '@/ai/components/LoadingBar.vue';
import { setup } from '@/ai/components/chatWindow.setup';
import UserMessage from '@/ai/components/UserMessage.vue';
import BotMessage from '@/ai/components/BotMessage.vue';
import { RESIZE_DIRECTION } from '@/ai/utils/defined';
import SlideDetail from '@/common/components/templates/slideDetail/SlideDetail.vue';
import { useChatStore } from '@/ai/store/chat';
import { useChatConversationsStore } from '@/ai/store/chatConversations';
import { useMyInfoStore } from '@/config/stores/my-info';
import ConversationList from '@/ai/components/ConversationList.vue';
import TargetPickerLayer from '@/ai/components/TargetPickerLayer.vue';

const {
  t,
  size,
  position,
  userInput,
  container,
  inputRef,
  isLoading,
  chatMessages,
  displayMessages,
  hasMoreHistory,
  hiddenHistoryCount,
  showEarlierMessages,
  isChatWindowOpen,
  isShowDetail,
  handleMessageClick,
  handleScroll,
  onUserScrollIntent,
  showScrollToBottom,
  scrollToBottom,
  toggleChat,
  sendMessage,
  startResize,
  stopDrag,
  startDrag,
  onEnter,
  shouldRenderSlideDetail,
  resetConversation,
  onSuggestionClick,
  onStopStreaming,
} = setup();

/**
 * 헤더의 "초기화" 버튼. 사용자에게 확인 후 store 의 resetConversation 호출 →
 * backend history + front chatMessages 를 모두 비움.
 */
const onClickReset = async () => {
  if (chatMessages.value.length === 0) return;
  const confirmed = window.confirm(t('WORD.CHATBOT.WINDOW.NEW_CHAT_CONFIRM'));
  if (!confirmed) return;
  await resetConversation();
};

const convStore = useChatConversationsStore();
const { panelOpen: convPanelOpen, switching: convSwitching } = storeToRefs(convStore);
const { myInfo: userInfo } = storeToRefs(useMyInfoStore());

// 타겟 선택 ('+' 버튼) — feature: chatbot-target-conversation
const chatStore = useChatStore();
const { selectedTargets } = storeToRefs(chatStore);
const isTargetPickerOpen = ref(false);
const toggleTargetPicker = () => {
  isTargetPickerOpen.value = !isTargetPickerOpen.value;
};
const closeTargetPicker = () => {
  isTargetPickerOpen.value = false;
};
/** 선택 칩 레벨 색상 클래스. */
const chipLevelClass = (level?: string): string => {
  if (level === 'CRITICAL') return 'chat-window__chip--critical';
  if (level === 'WARNING') return 'chat-window__chip--warning';
  return '';
};

const onTogglePanel = () => {
  convStore.togglePanel();
};

/** [+새대화] — 즉시 새 대화 생성 + active 전환, 본문 비움. confirm 없음 (가벼운 액션). */
const onClickNewConversation = async () => {
  // 재진입 가드 — 연속 클릭 시 createNew 가 병렬 실행되고, 공용 isLoading 을 잠금으로 쓰면
  // 먼저 끝난 요청이 해제해 버린다 (3차 리뷰 P1). 전용 switching 으로 잠그고 진입 자체를 차단.
  if (convSwitching.value) return;
  const uid = String(userInfo.value?.userId ?? -1);
  if (uid === '-1') return;
  // 진행 중 스트림을 끊고 전환 — 늦은 conversation 통보가 active 를 이전 대화로 되돌리는 것 방지 (재리뷰 P1)
  chatStore.cancelStreaming();
  convStore.switching = true;
  try {
    const newId = await convStore.createNew(uid);
    if (newId) {
      chatStore.clearMessages();
      convStore.closePanel();
    }
  } finally {
    convStore.switching = false;
  }
};

const { dockMode, dockWidth } = storeToRefs(chatStore);
const { toggleDockMode, setDockWidth } = chatStore;
const isDocked = computed(() => dockMode.value === 'docked');

/** 가장 마지막 메시지가 streaming 중인지 → 헤더 status pill 점멸. */
const isStreamingActive = computed(() => {
  const last = chatMessages.value[chatMessages.value.length - 1];
  return !!last && !!last.isPartial;
});

/** docked 모드일 때만 활성: 좌측 가장자리를 drag 해서 패널 폭 조절. */
const isResizingDock = ref(false);
let dragStartX = 0;
let dragStartWidth = 0;
const onDockResizeStart = (e: MouseEvent) => {
  if (!isDocked.value) return;
  e.preventDefault();
  isResizingDock.value = true;
  dragStartX = e.clientX;
  dragStartWidth = dockWidth.value;
  window.addEventListener('mousemove', onDockResizeMove);
  window.addEventListener('mouseup', onDockResizeEnd, { once: true });
};
const onDockResizeMove = (e: MouseEvent) => {
  if (!isResizingDock.value) return;
  // 좌측 핸들 → 좌측으로 끌면 폭 증가, 우측으로 끌면 감소
  const delta = dragStartX - e.clientX;
  setDockWidth(dragStartWidth + delta);
};
const onDockResizeEnd = () => {
  isResizingDock.value = false;
  window.removeEventListener('mousemove', onDockResizeMove);
};
onUnmounted(() => {
  window.removeEventListener('mousemove', onDockResizeMove);
});

/** docked 일 때 메인 콘텐츠가 패널만큼 비켜서도록 body 에 CSS variable + 클래스 토글. */
const applyDockEnvVar = () => {
  document.documentElement.style.setProperty(
    '--chatbot-dock-width',
    isDocked.value && isChatWindowOpen.value ? `${dockWidth.value}px` : '0px',
  );
  document.documentElement.classList.toggle(
    'chatbot-docked',
    isDocked.value && isChatWindowOpen.value,
  );
};
onMounted(applyDockEnvVar);
onUnmounted(() => {
  document.documentElement.style.setProperty('--chatbot-dock-width', '0px');
  document.documentElement.classList.remove('chatbot-docked');
});
const _watchAll = computed(() => `${isDocked.value}|${dockWidth.value}|${isChatWindowOpen.value}`);
watch(_watchAll, applyDockEnvVar, { immediate: true });

/** Enter 전송 / Shift+Enter 줄바꿈 / IME 조합 중에는 무시 */
const onKeydown = (e: KeyboardEvent) => {
  if (e.key !== 'Enter') return;
  if (e.shiftKey) return; // 줄바꿈 허용
  if (e.isComposing) return;
  e.preventDefault();
  onEnter(e);
};

/**
 * 챗봇 본문 폰트 크기 조절 — 시각 약자 / 고해상도 화면 사용자 보조.
 * scale 1.0 = 기본(14px). 0.85~1.6 까지 6단계 (0.85 / 1.0 / 1.15 / 1.3 / 1.45 / 1.6).
 * localStorage 'chatFontScale' 에 저장하여 새로고침 후에도 유지.
 * 적용 방식: chat-window__wrapper 에 --chat-font-scale CSS var 주입 → 본문 font-size em 단위.
 */
const FONT_SCALES = [0.85, 1.0, 1.15, 1.3, 1.45, 1.6] as const;
const fontScale = ref<number>(1.0);
const loadFontScale = () => {
  try {
    const stored = localStorage.getItem('chatFontScale');
    if (stored) {
      const v = parseFloat(stored);
      if (FONT_SCALES.includes(v as (typeof FONT_SCALES)[number])) fontScale.value = v;
    }
  } catch {
    /* localStorage 차단 환경 — 기본값 유지 */
  }
};
const persistFontScale = (v: number) => {
  try {
    localStorage.setItem('chatFontScale', String(v));
  } catch {
    /* ignore */
  }
};
const incFontScale = () => {
  const idx = FONT_SCALES.indexOf(fontScale.value as (typeof FONT_SCALES)[number]);
  const next = FONT_SCALES[Math.min(FONT_SCALES.length - 1, idx + 1)];
  fontScale.value = next;
  persistFontScale(next);
};
const decFontScale = () => {
  const idx = FONT_SCALES.indexOf(fontScale.value as (typeof FONT_SCALES)[number]);
  const next = FONT_SCALES[Math.max(0, idx - 1)];
  fontScale.value = next;
  persistFontScale(next);
};
const isMaxFontScale = computed(() => fontScale.value >= FONT_SCALES[FONT_SCALES.length - 1]);
const isMinFontScale = computed(() => fontScale.value <= FONT_SCALES[0]);
onMounted(loadFontScale);
</script>

<template>
  <div
    v-show="isChatWindowOpen"
    class="chat-window__wrapper clickoutside-ignore"
    :class="{
      'chat-window__wrapper--docked': isDocked,
      'chat-window__wrapper--floating': !isDocked,
    }"
    :style="{
      ...(isDocked
        ? { width: dockWidth + 'px' }
        : {
            top: position.top + 'px',
            left: position.left + 'px',
            width: size.width + 'px',
            height: size.height + 'px',
          }),
      '--chat-font-scale': fontScale,
    }"
  >
    <!-- docked 모드 전용 좌측 resize 핸들 — 패널 가로폭만 조절 -->
    <div
      v-if="isDocked"
      class="chat-window__dock-resize"
      :class="{ 'chat-window__dock-resize--active': isResizingDock }"
      @mousedown="onDockResizeStart"
    />

    <header
      class="chat-window__header"
      :class="{ 'chat-window__header--docked': isDocked }"
      @mousedown="!isDocked && startDrag($event)"
      @mouseup="!isDocked && stopDrag()"
    >
      <div class="chat-window__header__brand">
        <span class="chat-window__header__avatar">
          <i class="chat-window__header__avatar-inner" />
        </span>
        <div class="chat-window__header__title">
          <span class="chat-window__header__name">exemONE Assistant</span>
          <span
            class="chat-window__header__status"
            :class="{ 'chat-window__header__status--streaming': isStreamingActive }"
          >
            <span class="chat-window__header__status-dot" />
            {{
              isStreamingActive
                ? t('WORD.CHATBOT.WINDOW.RESPONDING')
                : t('WORD.CHATBOT.WINDOW.PREPARED')
            }}
          </span>
        </div>
      </div>
      <div class="chat-window__header__actions">
        <!-- 폰트 작게/크게 — 시각 보조. localStorage 'chatFontScale' 에 저장. -->
        <button
          class="chat-window__icon-btn chat-window__icon-btn--glyph"
          :class="{ 'chat-window__icon-btn--disabled': isMinFontScale }"
          :disabled="isMinFontScale"
          :title="t('WORD.CHATBOT.WINDOW.FONT_SMALLER')"
          :aria-label="t('WORD.CHATBOT.WINDOW.FONT_SMALLER')"
          @click="decFontScale"
        >
          A−
        </button>
        <button
          class="chat-window__icon-btn chat-window__icon-btn--glyph"
          :class="{ 'chat-window__icon-btn--disabled': isMaxFontScale }"
          :disabled="isMaxFontScale"
          :title="t('WORD.CHATBOT.WINDOW.FONT_LARGER')"
          :aria-label="t('WORD.CHATBOT.WINDOW.FONT_LARGER')"
          @click="incFontScale"
        >
          A+
        </button>
        <!-- 대화 목록 토글 -->
        <button
          class="chat-window__icon-btn chat-window__icon-btn--glyph"
          :title="t('WORD.CHATBOT.CONVERSATION.TITLE')"
          :aria-label="t('WORD.CHATBOT.WINDOW.CONVERSATION_LIST_OPEN')"
          :aria-expanded="convPanelOpen"
          aria-controls="chatbot-conversation-list"
          @click="onTogglePanel"
        >
          ☰
        </button>
        <!-- 새 대화 -->
        <button
          class="chat-window__icon-btn chat-window__icon-btn--glyph"
          :title="t('WORD.CHATBOT.WINDOW.NEW_CHAT')"
          :aria-label="t('WORD.CHATBOT.WINDOW.NEW_CHAT')"
          :disabled="convSwitching"
          @click="onClickNewConversation"
        >
          +
        </button>
        <button
          class="chat-window__icon-btn chat-window__reset-btn"
          :class="{
            'chat-window__icon-btn--disabled':
              chatMessages.length === 0 || isLoading || convSwitching,
          }"
          :disabled="chatMessages.length === 0 || isLoading || convSwitching"
          :title="t('WORD.CHATBOT.WINDOW.RESET_CHAT')"
          @click="onClickReset"
        >
          <ev-icon icon="ev-icon-refresh" />
        </button>
        <button
          class="chat-window__icon-btn chat-window__icon-btn--glyph"
          :title="
            isDocked ? t('WORD.CHATBOT.WINDOW.TO_FLOATING') : t('WORD.CHATBOT.WINDOW.TO_DOCKED')
          "
          @click="toggleDockMode"
        >
          {{ isDocked ? '⤢' : '⇥' }}
        </button>
        <button
          class="chat-window__icon-btn chat-window__close-btn"
          :title="t('WORD.CLOSE')"
          @click="toggleChat"
        >
          <ev-icon icon="icon-close" />
        </button>
      </div>
    </header>

    <!-- 대화 목록 패널 — 토글 ON 시 본문 위에 overlay -->
    <conversation-list />

    <div class="chat-window__scroll-area">
      <div
        ref="container"
        class="chat-window__content"
        @mousedown="startDrag"
        @mouseup="stopDrag"
        @scroll="handleScroll"
        @wheel.passive="onUserScrollIntent"
        @touchstart.passive="onUserScrollIntent"
        @keydown="onUserScrollIntent"
      >
        <button
          v-if="hasMoreHistory"
          type="button"
          class="chat-window__content__more"
          @click="showEarlierMessages"
        >
          {{ t('WORD.CHATBOT.WINDOW.LOAD_MORE', { count: hiddenHistoryCount }) }}
        </button>
        <div
          v-for="(message, idx) in displayMessages"
          :key="message.id ?? idx"
          class="chat-window__content__message"
        >
          <user-message
            v-if="message.sender !== 'chatbot'"
            :message="message"
          />
          <bot-message
            v-else
            :message="message"
            @message-click="handleMessageClick"
            @suggestion-click="onSuggestionClick"
          />
        </div>
        <loading-bar v-if="isLoading && !isStreamingActive" />
      </div>
      <!-- 스크롤이 바닥이 아닐 때만 노출되는 '맨 아래로' 버튼 (우하단) -->
      <transition name="chat-to-bottom">
        <button
          v-if="showScrollToBottom"
          type="button"
          class="chat-window__to-bottom"
          :title="t('WORD.CHATBOT.WINDOW.SCROLL_BOTTOM')"
          :aria-label="t('WORD.CHATBOT.WINDOW.SCROLL_BOTTOM')"
          @click="scrollToBottom"
        >
          <ev-icon icon="ev-icon-s-arrow-down" />
        </button>
      </transition>
    </div>

    <div class="chat-window__input-section">
      <target-picker-layer
        v-if="isTargetPickerOpen"
        class="chat-window__target-picker"
        @close="closeTargetPicker"
      />

      <!-- 선택 타겟 칩 (선택 0개면 미표시).
           공용 TagListInput 대신 자작 칩: 알람 레벨별 BEM modifier 색상(critical/warning)이 필요. -->
      <div
        v-if="selectedTargets.length"
        class="chat-window__selected-targets"
      >
        <span
          v-for="target in selectedTargets"
          :key="target.targetId"
          class="chat-window__chip"
          :class="chipLevelClass(target.alarmLevel)"
          :title="target.name"
        >
          <span class="chat-window__chip-name">{{ target.name }}</span>
          <button
            type="button"
            class="chat-window__chip-remove"
            :aria-label="t('WORD.CHATBOT.WINDOW.TARGET_REMOVE')"
            @click="chatStore.removeSelectedTarget(target.targetId)"
          >
            <ev-icon icon="icon-close" />
          </button>
        </span>
      </div>

      <div class="chat-window__input-wrap">
        <button
          type="button"
          class="chat-window__target-btn"
          :class="{ 'chat-window__target-btn--active': isTargetPickerOpen }"
          :aria-label="t('WORD.SELECT_TARGET')"
          :title="t('WORD.CHATBOT.WINDOW.TARGET_SELECT_TITLE')"
          @click="toggleTargetPicker"
        >
          <ev-icon icon="ev-icon-plus" />
          <span
            v-if="selectedTargets.length"
            class="chat-window__target-btn-badge"
            >{{ selectedTargets.length }}</span
          >
        </button>
        <textarea
          ref="inputRef"
          v-model="userInput"
          :disabled="isLoading || convSwitching"
          rows="1"
          :placeholder="t('WORD.CHATBOT.WINDOW.INPUT_PLACEHOLDER')"
          class="chat-window__input-section__input"
          @keydown="onKeydown"
        />
        <!-- 응답 생성 중에는 전송 대신 중지 — 전송 버튼은 어차피 disabled 라 자리를 그대로 쓴다. -->
        <button
          v-if="isStreamingActive"
          type="button"
          class="chat-window__send-btn chat-window__send-btn--stop"
          :aria-label="t('WORD.CHATBOT.WINDOW.STOP_RESPONSE')"
          :title="t('WORD.CHATBOT.WINDOW.STOP_RESPONSE')"
          @click="onStopStreaming"
        >
          <ev-icon icon="ev-icon-s-stop" />
        </button>
        <button
          v-else
          class="chat-window__send-btn"
          :class="{
            'chat-window__send-btn--disabled': !userInput.trim() || isLoading || convSwitching,
          }"
          :disabled="!userInput.trim() || isLoading || convSwitching"
          @click="sendMessage"
        >
          <ev-icon icon="icon-arrow-circle-up" />
        </button>
      </div>
    </div>

    <div class="chat-window__description">
      {{ t('DESC.AI.CHAT_AVAILABLE') }}
    </div>
    <div
      v-for="direction in RESIZE_DIRECTION"
      v-show="!isDocked"
      :key="direction"
      class="resize-handle"
      :class="direction"
      @mousedown="(e) => startResize(e, direction)"
    />
  </div>
  <slide-detail
    v-if="shouldRenderSlideDetail"
    v-model:is-visible="isShowDetail"
  />
</template>

<style scoped lang="scss">
.chat-window {
  &__wrapper {
    /* 폰트 사이즈 조절 — 헤더 A−/A+ 버튼이 --chat-font-scale (0.85~1.6) 을 주입.
       본문/입력 영역만 영향, 헤더/타이틀 등 chrome 은 고정 유지. */
    --chat-font-scale: 1;

    display: flex;
    position: fixed;
    z-index: $on-chat-window;
    flex-direction: column;
    overflow: hidden;
    background: var(--color-gray-08-01);
    box-shadow:
      0 24px 64px -12px var(--ai-chat-window-shadow-color),
      0 4px 16px -4px var(--ai-chat-window-shadow-color),
      inset 0 0 0 1px rgb(255, 255, 255, 0.04);

    .chat-window__content,
    .chat-window__input-section {
      font-size: calc(13px * var(--chat-font-scale));
    }

    /* 본문 안의 모든 텍스트 요소(메시지/리스트/inline code/표 등) 가 부모 font-size 따라가도록.
       명시적 font-size 가 본문 SCSS 에 있던 곳은 em 으로 환산. */
    .chat-window__content :is(p, li, td, th, pre, code, blockquote, .text-message__body) {
      font-size: 1em;
    }
  }

  &__wrapper--floating {
    border-radius: 16px;
  }

  &__wrapper--docked {
    inset: 0 0 0 auto;
    height: auto !important;
    border-left: 1px solid var(--color-gray-05-03);
    border-radius: 0;
    box-shadow:
      -8px 0 24px -8px var(--ai-chat-window-shadow-color),
      -2px 0 8px -2px var(--ai-chat-window-shadow-color);
  }

  &__dock-resize {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -3px;
    z-index: 11;
    width: 6px;
    cursor: ew-resize;
    transition: background-color 0.15s ease;

    &:hover,
    &--active {
      background: rgb(60, 142, 255, 0.35);
    }
  }

  &__header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    cursor: move;
    user-select: none;
    border-bottom: 1px solid var(--color-gray-05-03);
    background: linear-gradient(
      120deg,
      rgb(60, 142, 255, 0.18) 0%,
      rgb(108, 79, 255, 0.18) 50%,
      rgb(37, 99, 235, 0.12) 100%
    );
  }

  &__header--docked {
    cursor: default;
  }

  &__header__actions {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: 8px;
    background: none;
    font-size: 13px;
    line-height: 1;
    color: var(--color-gray-02-10);
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      color 0.15s ease;

    &:hover {
      border-color: var(--color-gray-05-03);
      background: rgb(255, 255, 255, 0.05);
      color: var(--color-gray-00-11);
    }
    &:active {
      border-color: var(--color-blue-05);
      background-color: var(--color-blue-09-01);
    }

    .ev-icon {
      width: 14px;
      height: 14px;
      background-color: currentcolor;
    }
  }

  &__icon-btn--glyph {
    font-size: 16px;
    font-weight: 600;
  }

  &__icon-btn--disabled,
  &__icon-btn[disabled] {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      border-color: transparent;
      background: none;
      color: var(--color-gray-02-10);
    }
  }

  &__header__brand {
    display: flex;
    gap: 10px;
    align-items: center;
    min-width: 0;
  }

  &__header__avatar {
    position: relative;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, #6c4fff 0%, #3c8eff 60%, #2563eb 100%);
    box-shadow: 0 4px 14px -2px rgb(60, 142, 255, 0.55);
  }

  &__header__avatar-inner {
    position: absolute;
    border-radius: 50%;
    background-color: rgb(255, 255, 255, 0.95);
    background-image: url('@/common/assets/ai/chat-bot.svg');
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    inset: 5px;
  }

  &__header__title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__header__name {
    font-size: 13.5px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: -0.1px;
    color: var(--color-gray-00-11);
  }

  &__header__status {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    font-size: 11px;
    font-weight: 500;
    line-height: 14px;
    color: var(--color-gray-02-11);
  }

  &__header__status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-green-05-05);
    box-shadow: 0 0 0 2px rgb(35, 192, 155, 0.18);
  }

  &__header__status--streaming &__header__status-dot,
  .chat-window__header__status--streaming .chat-window__header__status-dot {
    background: var(--color-blue-04-04);
    box-shadow: 0 0 0 2px rgb(60, 142, 255, 0.22);
    animation: chat-status-pulse 1.2s ease-in-out infinite;
  }

  @keyframes chat-status-pulse {
    50% {
      opacity: 0.6;
      transform: scale(0.7);
    }
  }

  &__content {
    display: flex;
    position: relative;
    flex-direction: column;
    flex-grow: 1;
    gap: 12px;
    min-height: 0; /* 중첩 flex(scroll-area > content) 안에서 content 가 줄어들어 스크롤되도록 */
    padding: 16px 16px 8px;
    overflow: hidden auto;
    background:
      radial-gradient(circle at 0% 0%, rgb(60, 142, 255, 0.05), transparent 45%),
      radial-gradient(circle at 100% 100%, rgb(108, 79, 255, 0.05), transparent 45%);

    &__more {
      flex-shrink: 0;
      align-self: center;
      padding: 4px 14px;
      cursor: pointer;
      border: 1px solid var(--color-gray-05-05, #e0e0e0);
      border-radius: 12px;
      background: var(--config-group-box-bg-color, #ffffff);
      font-size: 12px;
      color: var(--sub-font-color, #666666);

      &:hover {
        background: var(--config-section-body-bg-color, #f5f5f5);
      }
    }

    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      border: 2px solid transparent;
      border-radius: 999px;
      background-color: var(--color-gray-05-03);
      background-clip: padding-box;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: var(--color-gray-04-04);
      background-clip: padding-box;
    }
  }

  /* 본문 스크롤 영역 + '맨 아래로' 버튼의 포지셔닝 컨텍스트 (스크롤 컨테이너 밖, 고정 오버레이) */
  &__scroll-area {
    display: flex;
    position: relative;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }

  &__to-bottom {
    display: flex;
    position: absolute;
    right: 14px;
    bottom: 12px;
    z-index: 2;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    cursor: pointer;
    border: 1px solid var(--color-gray-05-03);
    border-radius: 50%;
    background: var(--color-gray-08-01);
    box-shadow: 0 4px 12px -2px var(--ai-chat-window-shadow-color);
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      transform 0.12s ease;

    &:hover {
      border-color: var(--color-blue-04-04);
      background: var(--color-gray-07-00);
      transform: translateY(-1px);
    }
    &:active {
      transform: translateY(0);
    }

    .ev-icon {
      width: 16px;
      height: 16px;
      background-color: var(--color-blue-04-04);
    }
  }

  .chat-to-bottom-enter-active,
  .chat-to-bottom-leave-active {
    transition:
      opacity 0.15s ease,
      transform 0.15s ease;
  }
  .chat-to-bottom-enter-from,
  .chat-to-bottom-leave-to {
    opacity: 0;
    transform: translateY(6px);
  }

  &__close-btn {
    display: flex;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: 8px;
    background: none;
    transition:
      background 0.15s ease,
      border-color 0.15s ease;

    &:hover {
      border-color: var(--color-gray-05-03);
      background: rgb(255, 255, 255, 0.05);
    }
    &:active {
      border-color: var(--color-blue-05);
      background-color: var(--color-blue-09-01);
    }

    .icon-close {
      width: 14px;
      height: 14px;
      background-color: var(--color-gray-02-10);
    }
  }

  &__input-section {
    position: relative;
    flex-shrink: 0;
    padding: 10px 14px 6px;
  }

  /* 타겟 선택 팝오버 — 입력창 위로 띄움 (feature: chatbot-target-conversation) */
  &__target-picker {
    position: absolute;
    right: 14px;
    bottom: calc(100% - 6px);
    left: 14px;
    z-index: 5;
    animation: chat-target-picker-in 0.14s ease;
  }

  @keyframes chat-target-picker-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &__selected-targets {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;
  }

  &__chip {
    display: inline-flex;
    gap: 3px;
    align-items: center;
    max-width: 150px;
    padding: 2px 4px 2px 9px;
    border-radius: 11px;
    background: var(--color-gray-05-03);
    font-size: 11px;
    font-weight: 500;
    color: var(--main-font-color);

    // 레벨 tint — 선택 칩만 봐도 위험 타겟을 구분.
    &--critical {
      background: var(--color-red-09-09);
      color: var(--color-red-05-05);
    }
    &--warning {
      background: var(--color-yellow-06-04);
      color: var(--color-yellow-03-07);
    }
  }

  &__chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chip-remove {
    display: inline-flex;
    align-items: center;
    padding: 1px;
    opacity: 0.6;
    cursor: pointer;
    border: none;
    border-radius: 50%;
    background: none;
    color: inherit;

    &:hover {
      opacity: 1;
    }

    .icon-close {
      width: 11px;
      height: 11px;
    }
  }

  &__target-btn {
    display: flex;
    position: relative;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    cursor: pointer;
    border: 1px solid var(--color-gray-05-03);
    border-radius: 8px;
    background: transparent;
    color: var(--main-font-color);
    transition: background 0.12s ease;

    &:hover:not(:disabled) {
      background: var(--color-gray-05-03);
    }
    &--active {
      border-color: var(--color-blue-04-04);
      background: var(--color-blue-09-01);
      color: var(--color-blue-04-04);
    }
    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .ev-icon-plus {
      width: 16px;
      height: 16px;
    }
  }

  &__target-btn-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    border-radius: 7px;
    background: var(--color-blue-04-04);
    font-size: 9px;
    font-weight: 600;
    line-height: 14px;
    color: #ffffff;
    text-align: center;
  }

  &__input-wrap {
    display: flex;
    position: relative;
    gap: 6px;
    align-items: flex-end;
    padding: 6px 6px 6px 12px;
    border: 1px solid var(--color-gray-05-03);
    border-radius: 14px;
    background: var(--color-gray-09-00);
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &:focus-within {
      border-color: var(--color-blue-04-04);
      box-shadow: 0 0 0 3px rgb(60, 142, 255, 0.14);
    }
  }

  &__input-section__input {
    flex-grow: 1;
    min-height: 28px;
    max-height: 120px;
    padding: 6px 0;
    resize: none;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.5;
    color: var(--main-font-color);

    &::placeholder {
      color: var(--color-gray-03-11);
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  &__send-btn {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    cursor: pointer;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #3c8eff 0%, #6c4fff 100%);
    box-shadow: 0 6px 16px -4px rgb(60, 142, 255, 0.55);
    transition:
      transform 0.12s ease,
      box-shadow 0.12s ease,
      opacity 0.15s ease;

    &:hover:not(:disabled) {
      box-shadow: 0 10px 22px -4px rgb(60, 142, 255, 0.6);
      transform: translateY(-1px);
    }
    &:active:not(:disabled) {
      transform: translateY(0);
    }

    .icon-arrow-circle-up {
      width: 18px;
      height: 18px;
      background-color: #ffffff;
    }
  }

  // 정지 글리프는 여백 없이 꽉 찬 사각형이라 화살표와 같은 18px 이면 과하게 커 보인다.
  &__send-btn--stop .ev-icon-s-stop {
    width: 14px;
    height: 14px;
    background-color: #ffffff;
  }

  &__send-btn--disabled,
  &__send-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  &__description {
    flex-shrink: 0;
    padding: 0 16px 12px;
    font-size: 11px;
    font-weight: 400;
    line-height: 16px;
    color: var(--color-gray-02-11);
    white-space: pre-line;
  }
}
.resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  background: transparent;

  &.top {
    top: -5px;
    left: 0;
    width: 100%;
    height: 10px;
    cursor: ns-resize;
  }

  &.bottom {
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 10px;
    cursor: ns-resize;
  }

  &.left {
    top: 0;
    left: -5px;
    width: 10px;
    height: 100%;
    cursor: ew-resize;
  }

  &.right {
    top: 0;
    right: -5px;
    width: 5px;
    height: 100%;
    cursor: ew-resize;
    pointer-events: auto;
  }

  &.top-left {
    top: -5px;
    left: -5px;
    width: 10px;
    height: 10px;
    cursor: nwse-resize;
  }

  &.top-right {
    top: -5px;
    right: -5px;
    width: 10px;
    height: 10px;
    cursor: nesw-resize;
  }

  &.bottom-left {
    bottom: -5px;
    left: -5px;
    width: 10px;
    height: 10px;
    cursor: nesw-resize;
  }

  &.bottom-right {
    right: -5px;
    bottom: -5px;
    width: 10px;
    height: 10px;
    cursor: nwse-resize;
  }
}
</style>

<!--
  본문/입력 영역 폰트 사이즈 scale — 자식 컴포넌트(BotMessage non-scoped, UserMessage scoped) 의
  자체 absolute font-size 를 override 해야 하므로 NON-SCOPED 블록 + .chat-window__wrapper 후손
  selector 로 한정. !important 는 자식의 명시적 px 값을 덮기 위해 필요.
  ─ scale 가 적용된 base font-size = 13px * --chat-font-scale
  ─ 헤더/타이틀 등 chrome 은 scoped 블록에서 고정값 유지하므로 영향 없음.
-->
<style lang="scss">
.chat-window__wrapper {
  /* :style 바인딩으로 들어오는 --chat-font-scale 의 fallback. */
  --chat-font-scale: 1;
}
.chat-window__wrapper .chat-window__content,
.chat-window__wrapper .chat-window__input-section,
.chat-window__wrapper .chat-window__input-section__input,
.chat-window__wrapper .user-message,
.chat-window__wrapper .user-message__text,
.chat-window__wrapper .bot-message,
.chat-window__wrapper .text-message__body,
.chat-window__wrapper .text-message__body p,
.chat-window__wrapper .text-message__body li,
.chat-window__wrapper .text-message__body td,
.chat-window__wrapper .text-message__body th,
.chat-window__wrapper .text-message__body code,
.chat-window__wrapper .text-message__body pre,
.chat-window__wrapper .text-message__body blockquote {
  font-size: calc(13px * var(--chat-font-scale)) !important;
  line-height: 1.55;
}

/* 코드/인라인은 약간 작게 유지하되 scale 반영. */
.chat-window__wrapper .text-message__body code:not(pre code) {
  font-size: calc(11.5px * var(--chat-font-scale)) !important;
}
.chat-window__wrapper .text-message__body h1,
.chat-window__wrapper .text-message__body h2,
.chat-window__wrapper .text-message__body h3 {
  font-size: calc(15px * var(--chat-font-scale)) !important;
}
.chat-window__wrapper .text-message__body h4,
.chat-window__wrapper .text-message__body h5,
.chat-window__wrapper .text-message__body h6 {
  font-size: calc(13.5px * var(--chat-font-scale)) !important;
}
</style>
